import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { ContactQueryApi } from '../../api/adminApi';
import './ContactQueriesAdmin.css';

const CONTACT_STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'in-progress', label: 'In progress' },
  { value: 'resolved', label: 'Resolved' }
];

const formatDate = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return value;
  }
};

const ContactQueriesAdmin = () => {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [statusDraft, setStatusDraft] = useState('new');
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusNotice, setStatusNotice] = useState('');

  const loadQueries = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await ContactQueryApi.getAll();
      setQueries(Array.isArray(data) ? data : []);
      if (selectedQuery) {
        const updated = data.find((item) => item.id === selectedQuery.id);
        setSelectedQuery(updated || null);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to load contact inquiries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQueries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedQuery) {
      setStatusDraft(selectedQuery.status || 'new');
      setStatusNotice('');
    }
  }, [selectedQuery]);

  const handleStatusChange = (event) => {
    setStatusDraft(event.target.value);
    setStatusNotice('');
  };

  const handleStatusUpdate = async () => {
    if (!selectedQuery) return;
    const currentStatus = selectedQuery.status || 'new';
    if (statusDraft === currentStatus) {
      return;
    }
    setStatusUpdating(true);
    setError('');
    setStatusNotice('');
    try {
      const updated = await ContactQueryApi.updateStatus(selectedQuery.id, statusDraft);
      setQueries((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      setSelectedQuery(updated);
      setStatusNotice('Status updated.');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to update status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const renderTable = () => {
    if (loading) {
      return <p>Loading contact inquiries…</p>;
    }
    if (!queries.length) {
      return <p>No contact inquiries yet.</p>;
    }
    return (
      <table className="queries-table">
        <thead>
          <tr>
            <th>Contact</th>
            <th>Subject</th>
            <th>Status</th>
            <th>Received</th>
            <th>Source</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {queries.map((query) => {
            const isSelected = selectedQuery?.id === query.id;
            return (
              <React.Fragment key={query.id}>
                <tr>
                  <td>
                    <div className="cell-stack">
                      <strong>{query.name}</strong>
                      <span>{query.email}</span>
                    </div>
                  </td>
                  <td>{query.subject || '—'}</td>
                  <td>
                    <span className={`status-pill status-${(query.status || 'new').toLowerCase()}`}>
                      {query.status || 'new'}
                    </span>
                  </td>
                  <td>{formatDate(query.createdAt)}</td>
                  <td>{query.source || 'contact'}</td>
                  <td>
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => {
                        if (isSelected) {
                          setSelectedQuery(null);
                          setStatusNotice('');
                        } else {
                          setSelectedQuery(query);
                        }
                      }}
                    >
                      {isSelected ? 'Hide details' : 'View details'}
                    </button>
                  </td>
                </tr>
                {isSelected && selectedQuery && (
                  <tr className="query-detail-row">
                    <td colSpan="6">
                      <div className="query-detail-card">
                        <div className="query-detail-header">
                          <div>
                            <h3>{selectedQuery.subject || 'Message'}</h3>
                            <p>
                              From <strong>{selectedQuery.name}</strong> · {selectedQuery.email}
                            </p>
                            <p className="query-detail-meta">
                              {selectedQuery.source || 'contact'} · {formatDate(selectedQuery.createdAt)}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="admin-secondary-btn"
                            onClick={() => {
                              setSelectedQuery(null);
                              setStatusNotice('');
                            }}
                          >
                            Close
                          </button>
                        </div>
                        <p className="query-detail-message">{selectedQuery.message}</p>
                        <div className="status-form">
                          <label htmlFor="contact-status">Status</label>
                          <div className="status-form-row">
                            <select
                              id="contact-status"
                              className="status-select"
                              value={statusDraft}
                              onChange={handleStatusChange}
                              disabled={statusUpdating}
                            >
                              {CONTACT_STATUS_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              className="admin-primary-btn"
                              onClick={handleStatusUpdate}
                              disabled={statusUpdating || statusDraft === (selectedQuery.status || 'new')}
                            >
                              {statusUpdating ? 'Updating…' : 'Save status'}
                            </button>
                          </div>
                          {statusNotice && <p className="status-notice success">{statusNotice}</p>}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <AdminLayout
      title="Contact Inquiries"
      description="Monitor every contact form submission across the site."
      actions={(
        <button type="button" className="admin-secondary-btn" onClick={loadQueries} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      )}
    >
      {error && <div className="admin-alert">{error}</div>}
      <div className="queries-table-wrapper">{renderTable()}</div>
    </AdminLayout>
  );
};

export default ContactQueriesAdmin;
